import {
  useApiServices,
  useAppContext,
  useCapabilityRegistryContext,
  useDataContext,
} from '@mobile-mapping/providers';
import { useConnectProject } from '@trimble/connect-project-providers';
import { PlanarPaneContent } from '@mobile-mapping/web3d';
import { StationsCapabilityProvider } from '@trimble/web3d-providers';
import {
  Accessor,
  createContext,
  createEffect,
  createSignal,
  on,
  JSX,
  useContext,
} from 'solid-js';

import { createPlanarStationLoadContextMemo } from './planar-station-load-context';
import { registerPlanarStationsLoadingEffect } from './planar-stations-loading-effect';
import {
  resetTilesetPrefetchState,
  TilesetPrefetchContext,
} from './tileset-prefetch';

export interface StationLoadContextValue {
  readonly tilesetPrefetchContext: Accessor<TilesetPrefetchContext>;
}

const StationLoadContext = createContext<StationLoadContextValue>();

export interface StationLoadProviderProps {
  readonly planarContents: Accessor<PlanarPaneContent[]>;
  readonly children: JSX.Element;
}

export function StationLoadProvider(
  props: StationLoadProviderProps
): JSX.Element {
  const { imagingApi, mobileMappingApi, scanDataApi } = useApiServices();
  const { selectedRegion, regions, selectedLocation } = useConnectProject();
  const { setDataState } = useDataContext();
  const { startImagesLoading, stopImagesLoading } = useAppContext();
  const capabilityRegistry = useCapabilityRegistryContext().capabilityRegistry;

  const [tilesetPrefetchContext, setTilesetPrefetchContext] =
    createSignal<TilesetPrefetchContext>({});

  createEffect(
    on(selectedLocation, () => {
      resetTilesetPrefetchState();
    })
  );

  const loadContext = createPlanarStationLoadContextMemo(
    () => props.planarContents(),
    regions,
    selectedRegion,
    selectedLocation
  );

  registerPlanarStationsLoadingEffect({
    loadContext,
    getCurrentPlanarContents: () => props.planarContents(),
    getStationsProvider: capabilityRegistry.get(StationsCapabilityProvider),
    setDataState,
    setTilesetPrefetchContext,
    imagingApi,
    mobileMappingApi,
    scanDataApi,
    startImagesLoading,
    stopImagesLoading,
  });

  return (
    <StationLoadContext.Provider value={{ tilesetPrefetchContext }}>
      {props.children}
    </StationLoadContext.Provider>
  );
}

export function useStationLoadContext(): StationLoadContextValue {
  const context = useContext(StationLoadContext);
  if (context === undefined) {
    throw new Error(
      'useStationLoadContext must be used within a StationLoadProvider'
    );
  }
  return context;
}
